import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { NgxPopupComponent } from '@components/shared/ngx-popups/ngx-popups/components/popup.component';
import { Company } from '@models/company';
import { AlertService } from '@services/alert.service';
import { TransferBorrowerService } from '@services/transfer-borrower.service';
import {
  EligibilityErrorCode,
  NO_SELECTION_COPY,
  SOURCE_NOT_FOUND_COPY,
  TransferErrorView,
  TransferResult,
  TransferStage,
} from '@services/transfer-borrower.types';

@Component({
  selector: 'app-transfer-borrower-modal',
  templateUrl: './transfer-borrower-modal.component.html',
  styleUrls: ['./transfer-borrower-modal.component.scss'],
  standalone: false,
})
export class TransferBorrowerModalComponent implements OnInit, OnDestroy {
  /** MANDATORY — auto-injected by NgxPopupService so the body can close itself. */
  @Input() popup!: NgxPopupComponent;

  /** Source borrower (test entity) whose data will be transferred. */
  @Input() source!: Company;

  /** Local mirror of the last emission from the search component. */
  selectedTarget: Company | null = null;

  /** Modal-local NO_SELECTION flag — never enters the service. */
  readonly noSelection$ = new BehaviorSubject<boolean>(false);

  /** Combined band error: server error wins, NO_SELECTION fills in when no server error. */
  readonly bandError$: Observable<TransferErrorView | null>;

  /** Convenience streams for the template. */
  readonly stage$;
  readonly acknowledged$;

  /** Expose enum to the template so *ngSwitchCase can reference it. */
  readonly TransferStage = TransferStage;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private service: TransferBorrowerService,
    private alertService: AlertService,
    private router: Router,
  ) {
    this.stage$ = this.service.stage$;
    this.acknowledged$ = this.service.acknowledged$;
    this.bandError$ = combineLatest([
      this.noSelection$,
      this.service.lastError$,
    ]).pipe(
      map(([noSel, serverErr]) => {
        if (serverErr) return serverErr;
        if (noSel) return { code: 'NO_SELECTION' as const, copy: NO_SELECTION_COPY };
        return null;
      }),
    );
  }

  ngOnInit(): void {
    this.service.open(this.source);

    // Toast-and-close dispatch for SOURCE_NOT_TEST / SOURCE_HAS_NO_DOCUMENTS.
    const TOAST_AND_CLOSE: Array<EligibilityErrorCode | string> = [
      EligibilityErrorCode.SOURCE_NOT_TEST,
      EligibilityErrorCode.SOURCE_HAS_NO_DOCUMENTS,
    ];
    this.service.lastError$
      .pipe(takeUntil(this.destroy$))
      .subscribe(err => {
        if (!err) return;
        if (TOAST_AND_CLOSE.includes(err.code)) {
          this.alertService.error(err.copy);
          this.popup.close();
        }
      });

    // Stage-transition dispatch for COMPLETE (success or SOURCE_NOT_FOUND recovery).
    // EXECUTION_FAILED stays in the modal — the Try again / Close buttons own that flow.
    this.service.stage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(stage => {
        if (stage !== TransferStage.COMPLETE) return;
        const result     = this.service.executeResult$.value;
        const validation = this.service.validation$.value;
        const targetUuid = validation?.target?.uuid;
        this.popup.close();
        if (targetUuid) {
          this.router.navigate(['/companies', targetUuid, 'financials', 'analysis'])
            .then(() => this._emitCompletionToast(result));
        } else {
          // Defensive: COMPLETE should never fire without a prior validation$ being set.
          // Emit the toast anyway and skip navigation.
          this._emitCompletionToast(result);
        }
      });
  }

  private _emitCompletionToast(result: TransferResult | null): void {
    if (result) {
      this.alertService.success(
        `Transfer complete. ${result.periodsTransferred} periods and `
        + `${result.documentsTransferred} documents transferred. Source borrower deleted.`,
      );
    } else {
      // SOURCE_NOT_FOUND recovery path.
      this.alertService.info(SOURCE_NOT_FOUND_COPY);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.service.reset();
  }

  onTargetSelected(target: Company | null): void {
    this.selectedTarget = target;
    // Any selection change (including deselect) dismisses the stale server
    // error band — the previous error referred to the previously-selected row.
    this.service.clearError();
    if (target) {
      this.noSelection$.next(false);
    }
  }

  onNext(): void {
    if (!this.selectedTarget) {
      this.noSelection$.next(true);
      return;
    }
    this.noSelection$.next(false);
    this.service.validate(this.source.uuid!, this.selectedTarget.uuid!);
  }

  onBack(): void {
    this.service.stage$.next(TransferStage.SEARCHING);
  }

  onSubmit(): void {
    this.service.submit();
  }

  onCancel(): void {
    this.popup.close();
  }

  onTryAgain(): void {
    this.service.submit();
  }

  isSearchPhase(s: TransferStage | null): boolean {
    return s === TransferStage.SEARCHING
        || s === TransferStage.VALIDATING
        || s === TransferStage.INELIGIBLE;
  }

  isConfirmPhase(s: TransferStage | null): boolean {
    return s === TransferStage.VALIDATED
        || s === TransferStage.EXECUTING;
  }
}
